import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { menuData, menuCategories } from '../data/menuData';
import { ArrowRight } from 'lucide-react';

const MenuItemCard = ({ item, name, desc }) => {
  return (
    <div
      className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[#d4af37]/40 hover:bg-white/10 transition-all duration-300 group flex flex-col"
      data-testid={name}
    >
      <div className="aspect-[4/3] bg-gray-900 relative overflow-hidden flex items-center justify-center">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <span className="text-[#d4af37]/40 text-3xl font-bold" style={{ fontFamily: "'Crimson Text', serif" }}>Ç</span>
        )}
      </div>
      <div className="p-3 md:p-4 flex-1 flex flex-col">
        <h4 className="text-white font-semibold text-sm md:text-base leading-tight mb-1">{item.name}</h4>
        {desc && <p className="text-white/50 text-xs mb-2 whitespace-pre-line">{desc}</p>}

        <p className="text-[#d4af37] font-bold text-base md:text-lg mt-auto">{item.price}</p>
      </div>
    </div>
  );
};

const MenuSection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [active, setActive] = useState(menuCategories[0].key);

  const getDesc = (item) => {
    if (language === 'en') return item.desc_en || item.desc_de || '';
    if (language === 'tr') return item.desc_tr || item.desc_de || '';
    return item.desc_de || '';
  };

  const items = menuData[active] || [];

  const catTitle = (c) => {
    if (language === 'en') return c.title_en || c.title_de;
    if (language === 'tr') return c.title_tr || c.title_de;
    return c.title_de;
  };

  return (
    <section id="menu" className="py-20 bg-gradient-to-b from-black to-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 pt-20">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Crimson Text', serif" }}>
            {t.menu_title}
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {t.menu_subtitle}
          </p>
        </div>

        {/* Category Tabs - yan yana */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10" data-testid="menu-category-tabs">
          {menuCategories.map((c) => (
            <button
              key={c.key}
              data-testid={`menu-tab-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`px-4 py-2 rounded-full text-sm md:text-base font-semibold transition-all duration-300 border ${
                active === c.key
                  ? 'bg-[#d4af37] text-black border-[#d4af37] shadow-lg shadow-[#d4af37]/20'
                  : 'bg-white/5 text-white/80 border-white/10 hover:border-[#d4af37]/40 hover:text-white'
              }`}
            >
              {catTitle(c)}
            </button>
          ))}
        </div>

        {/* Selected Category Items - compact grid */}
        <div
          key={active}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 items-start"
          data-testid="menu-items-grid"
        >
          {items.map((item, index) => (
            <MenuItemCard
              key={index}
              item={item}
              name={`menu-item-${active}-${index}`}
              desc={getDesc(item)}
            />
          ))}
        </div>

        {/* Order CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#d4af37]/20 to-[#f0c24d]/20 backdrop-blur-sm rounded-2xl p-8 border border-[#d4af37]/30">
          <div className="text-center">
            <p className="text-white/90 text-lg mb-6">
              {t.menu_note}
            </p>
            <a
              href="/bestellung"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#f0c24d] transition-all duration-300 transform hover:scale-105"
              data-testid="menu-order-cta"
            >
              {t.order_now}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
